<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
    <b-card
      v-else
      class="rounded-lg ledger-dense-form"
    >
      <h4 class="mb-50">
        {{ isEdit ? $t('ledger_item_edit') : $t('ledger_item_new') }}
      </h4>
      <b-row>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_item_code')">
            <b-form-input
              v-model="form.code"
              maxlength="32"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_item_name')">
            <b-form-input v-model="form.name" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_item_kind')">
            <b-form-select
              v-model="form.kind"
              :options="kindOptions"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_item_unit')">
            <b-form-input v-model="form.unit" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_item_group')">
            <b-form-input v-model="form.group_name" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_item_supplier')">
            <b-form-input
              v-model="form.supplier_name"
              :title="$t('ledger_item_supplier_hint')"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
        >
          <b-form-group :label="$t('ledger_item_finished_or_raw')">
            <b-form-input v-model="form.finished_or_raw" />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          lg="3"
          class="d-flex align-items-end"
        >
          <b-form-checkbox
            v-model="form.marked"
            class="mr-50"
          >
            {{ $t('ledger_item_marked') }}
          </b-form-checkbox>
          <b-form-checkbox v-model="form.in_use">
            {{ $t('ledger_item_in_use') }}
          </b-form-checkbox>
        </b-col>
        <b-col
          cols="6"
          lg="2"
        >
          <b-form-group :label="$t('ledger_item_description')">
            <b-form-input v-model="form.description" />
          </b-form-group>
        </b-col>
        <b-col
          v-for="field in priceFields"
          :key="field.key"
          cols="4"
          lg="2"
        >
          <b-form-group :label="field.label">
            <b-form-input
              v-model.number="form[field.key]"
              type="number"
              min="0"
              step="0.01"
            />
          </b-form-group>
        </b-col>
        <b-col
          v-if="showOpening"
          cols="4"
          lg="2"
        >
          <b-form-group :label="$t('ledger_item_opening_qty')">
            <b-form-input
              v-model.number="form.opening_qty"
              type="number"
              min="0"
              step="0.001"
              :title="$t('ledger_item_opening_hint')"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="4"
          lg="2"
        >
          <b-form-group :label="$t('ledger_item_requested_qty')">
            <b-form-input
              v-model.number="form.requested_qty"
              type="number"
              min="0"
              step="0.001"
            />
          </b-form-group>
        </b-col>
        <template v-if="isEdit">
          <b-col
            cols="4"
            lg="2"
          >
            <b-form-group :label="$t('ledger_item_received_qty')">
              <b-form-input
                :value="qty(item && item.received_qty)"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="4"
            lg="2"
          >
            <b-form-group :label="$t('ledger_item_issued_qty')">
              <b-form-input
                :value="qty(item && item.issued_qty)"
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="4"
            lg="2"
          >
            <b-form-group :label="$t('ledger_item_qty_on_hand')">
              <b-form-input
                :value="qty(item && item.qty_on_hand)"
                disabled
              />
            </b-form-group>
          </b-col>
        </template>
      </b-row>
      <p
        v-if="isEdit && !showOpening"
        class="text-muted small"
      >
        {{ $t('ledger_item_opening_locked') }}
      </p>
      <p
        v-if="isEdit"
        class="text-muted small"
      >
        {{ $t('ledger_item_qty_readonly') }}
      </p>
      <b-button
        variant="primary"
        class="rounded-pill"
        :disabled="saving || !canSave"
        @click="save"
      >
        {{ $t('ledger_assets_save') }}
      </b-button>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BButton, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { createItem, getItem, updateItem } from '@/api/accounting/items'
import {
  oneFrom, enumValue, formatLedgerQty, roundMoney, roundQty, apiErrorMessage,
} from '@/utils/ledger'

const TEXT_KEYS = ['group_name', 'supplier_name', 'unit', 'finished_or_raw', 'description']
const MONEY_KEYS = ['purchase_price', 'sale_price', 'average_cost', 'last_price', 'cost']

function emptyToNull(value) {
  const text = String(value || '').trim()
  return text || null
}

function moneyOrNull(value) {
  if (value === null || value === undefined || value === '') return null
  return roundMoney(value)
}

function qtyOrNull(value) {
  if (value === null || value === undefined || value === '') return null
  return roundQty(value)
}

function blankForm() {
  return {
    code: '',
    name: '',
    kind: 'service',
    group_name: '',
    supplier_name: '',
    unit: '',
    finished_or_raw: '',
    description: '',
    marked: false,
    in_use: true,
    purchase_price: null,
    sale_price: null,
    average_cost: null,
    last_price: null,
    cost: null,
    opening_qty: null,
    requested_qty: null,
  }
}

export default {
  name: 'ItemForm',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BButton, BSpinner,
  },
  data() {
    return {
      loading: false,
      saving: false,
      item: null,
      baseline: null,
      form: blankForm(),
    }
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id)
    },
    kindOptions() {
      return [
        { value: 'service', text: this.$t('ledger_item_kind_service') },
        { value: 'stock', text: this.$t('ledger_item_kind_stock') },
      ]
    },
    priceFields() {
      return [
        { key: 'purchase_price', label: this.$t('ledger_item_purchase_price') },
        { key: 'sale_price', label: this.$t('ledger_item_sale_price') },
        { key: 'average_cost', label: this.$t('ledger_item_average_cost') },
        { key: 'last_price', label: this.$t('ledger_item_last_price') },
        { key: 'cost', label: this.$t('ledger_item_cost') },
      ]
    },
    showOpening() {
      if (!this.isEdit) return true
      const received = Number(this.item && this.item.received_qty) || 0
      const issued = Number(this.item && this.item.issued_qty) || 0
      return received <= 0 && issued <= 0
    },
    canSave() {
      const code = String(this.form.code || '').trim()
      const name = String(this.form.name || '').trim()
      if (!code || code.length > 32 || !name) return false
      if (!['service', 'stock'].includes(this.form.kind)) return false
      if (this.isEdit && !Object.keys(this.changedPayload()).length) return false
      return true
    },
  },
  mounted() {
    if (this.isEdit) this.load()
  },
  methods: {
    qty(value) {
      return formatLedgerQty(value || 0, this.$i18n.locale)
    },
    normalized() {
      const body = {
        code: String(this.form.code || '').trim(),
        name: String(this.form.name || '').trim(),
        kind: this.form.kind,
        marked: Boolean(this.form.marked),
        in_use: Boolean(this.form.in_use),
      }
      TEXT_KEYS.forEach(key => {
        body[key] = emptyToNull(this.form[key])
      })
      MONEY_KEYS.forEach(key => {
        body[key] = moneyOrNull(this.form[key])
      })
      body.requested_qty = qtyOrNull(this.form.requested_qty)
      if (this.showOpening) body.opening_qty = qtyOrNull(this.form.opening_qty)
      return body
    },
    changedPayload() {
      const next = this.normalized()
      const prev = this.baseline || {}
      const payload = {}
      Object.keys(next).forEach(key => {
        if (next[key] !== prev[key]) payload[key] = next[key]
      })
      return payload
    },
    createPayload() {
      const next = this.normalized()
      const payload = {
        code: next.code,
        name: next.name,
        kind: next.kind,
        marked: next.marked,
        in_use: next.in_use,
      }
      TEXT_KEYS.forEach(key => {
        if (next[key]) payload[key] = next[key]
      })
      MONEY_KEYS.forEach(key => {
        if (next[key] !== null) payload[key] = next[key]
      })
      if (next.requested_qty !== null) payload.requested_qty = next.requested_qty
      if (next.opening_qty !== null) payload.opening_qty = next.opening_qty
      return payload
    },
    applyItem(item) {
      this.item = item
      this.form = {
        code: item.code || '',
        name: item.name || '',
        kind: enumValue(item.kind) || 'service',
        group_name: item.group_name || '',
        supplier_name: item.supplier_name || '',
        unit: item.unit || '',
        finished_or_raw: item.finished_or_raw || '',
        description: item.description || '',
        marked: Boolean(item.marked),
        in_use: Boolean(item.in_use),
        purchase_price: moneyOrNull(item.purchase_price),
        sale_price: moneyOrNull(item.sale_price),
        average_cost: moneyOrNull(item.average_cost),
        last_price: moneyOrNull(item.last_price),
        cost: moneyOrNull(item.cost),
        opening_qty: qtyOrNull(item.opening_qty),
        requested_qty: qtyOrNull(item.requested_qty),
      }
      this.baseline = this.normalized()
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon',
          variant,
        },
      })
    },
    async load() {
      this.loading = true
      try {
        const item = oneFrom(await getItem(this.$route.params.id))
        this.applyItem(item)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.canSave || this.saving) return
      this.saving = true
      try {
        const res = this.isEdit
          ? await updateItem(this.$route.params.id, this.changedPayload())
          : await createItem(this.createPayload())
        const item = oneFrom(res)
        this.toast(this.$t('ledger_item_saved'), 'success')
        const id = (item && item.id) || this.$route.params.id
        if (id) this.$router.push({ name: 'ledger-items-view', params: { id } })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss">
.ledger-dense-form .card-body { padding: 0.75rem 1rem; }
.ledger-dense-form .form-group { margin-bottom: 0.35rem; }
.ledger-dense-form .form-group > label {
  font-size: 0.75rem;
  margin-bottom: 0.1rem;
}
.ledger-dense-form .form-control,
.ledger-dense-form .custom-select {
  height: 1.85rem;
  min-height: 1.85rem;
  padding: 0.15rem 0.45rem;
  font-size: 0.8125rem;
}
</style>
